/*eslint complexity: ["error", 30]*/
/*eslint max-lines-per-function: ["error", 405]*/
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import {
  GetDictionaryDtoFromJSON,
  GetEstimateByMonthDto,
  GetPaeEstimateItemsDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { usePokCore } from '../../../common/hooks/usePokCore';
import FormSkeleton from '../../../app/components/FormSkeleton';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { PaePositionsGrid } from './PaePositionsGrid';
import { isPaePositionCorrect, setPaePositionTeam } from './PaeUtils';
import { PaeBulkParameterMapperForm } from './PaeBulkParameterMapperForm';

interface PaeImportFormProps {
  estimateByMonth: GetEstimateByMonthDto;
  project: GetProjectDto;
  show?: boolean;
  purchaseOnly: boolean;
  handleClose?: () => void;
  onCompleted: (paePositions: GetPaeEstimateItemsDto[]) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const PaeImportForm: React.FC<PaeImportFormProps> = props => {
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [paePositions, setPaePositions] = useState<GetPaeEstimateItemsDto[]>(
    [],
  );
  const [paeUrl, setPaeUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const notifications = useNotifications();
  const pok = usePokCore();
  const { t, tk } = useTranslation('projectLng');

  const refreshFunc = useCallback(() => {
    setIsLoading(true);
    pok.estimateItems
      .getEstimateItemsFromPae(
        props.project.number,
        props.estimateByMonth.date.getFullYear(),
        props.estimateByMonth.date.getMonth() + 1,
      )
      .then(data => {
        setPaePositions(data.positions);
        setPaeUrl(data.paeUrl);
        setSaveErrors([]);
        setShowSaveErrors(true);
        setIsLoading(false);
      })
      .catch(async errorResponse => {
        const errors = await responseErrors(errorResponse);
        setSaveErrors(errors);
        setShowSaveErrors(true);
        setIsLoading(false);
      });
  }, [pok.estimateItems, props.estimateByMonth.date, props.project.number]);

  useEffect(() => {
    if (props.show) {
      refreshFunc();
    }
  }, [props.show, refreshFunc]);

  const importSelectedPositions = () => {
    const selected = paePositions.filter(p =>
      selectedPositions.includes(p.idx),
    );
    if (selected.length === 0) {
      setSaveErrors([t(tk.estimates.noPositionSelected)]);
      setShowSaveErrors(true);
      return;
    }
    void importPositions(selected);
  };

  const importAllPositions = () => {
    void importPositions(paePositions.filter(isPaePositionCorrect));
  };

  const importPositions = async (positions: GetPaeEstimateItemsDto[]) => {
    props
      .onCompleted(positions)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      });
  };

  const handleOnRowClick = (key: string) => {
    let sel: string[];

    if (!selectedPositions.includes(key)) {
      sel = [...selectedPositions, key];
    } else {
      sel = selectedPositions.filter(o => o !== key);
    }
    setSelectedPositions(sel);
  };

  const refreshPositions = async () => {
    refreshFunc();
  };

  const openPae = async () => {
    if (paeUrl) {
      window.open(paeUrl, '_blank');
    }
  };

  const selectAll = () => {
    setSelectedPositions(
      paePositions.filter(isPaePositionCorrect).map(p => p.idx),
    );
  };

  const deselectAll = () => {
    setSelectedPositions([]);
  };

  const updateParams = (
    rowId: string,
    newPurchaseFormDictId: string,
    newPurchaseFormDictValue: string,
    newPurchaseFormDictValueEn: string,
    newCountryDictId: string,
    newCountryDictValue: string,
    newCountryDictValueEn: string,
    newBallDictId: string,
    newBallDictValue: string,
    newBallDictValueEn: string,
    newActionGroup: string,
    newPathPhaseDictId: string,
    newPathPhaseDictValue: string,
    newPathPhaseDictValueEn: string,
    newSpecialistTeamId: string,
    newSpecialistTeamName: string,
  ) => {
    paePositions.forEach(row => {
      if (row.idx === rowId) {
        if (newPurchaseFormDictId) {
          row.purchaseFormDict.id = newPurchaseFormDictId;
          row.purchaseFormDict.value = newPurchaseFormDictValue;
          row.purchaseFormDict.valueEn = newPurchaseFormDictValueEn;
        }

        if (newCountryDictId) {
          row.countryDict = row.countryDict
            ? row.countryDict
            : GetDictionaryDtoFromJSON({});
          row.countryDict.id = newCountryDictId;
          row.countryDict.value = newCountryDictValue;
          row.countryDict.valueEn = newCountryDictValueEn;
        }
        if (newBallDictId) {
          row.ballDict = row.ballDict
            ? row.ballDict
            : GetDictionaryDtoFromJSON({});
          row.ballDict.id = newBallDictId;
          row.ballDict.value = newBallDictValue;
          row.ballDict.valueEn = newBallDictValueEn;
        }
        row.actionGroup = newActionGroup || '';

        if (newPathPhaseDictId) {
          row.pathPhaseDict = row.pathPhaseDict
            ? row.pathPhaseDict
            : GetDictionaryDtoFromJSON({});
          row.pathPhaseDict.id = newPathPhaseDictId;
          row.pathPhaseDict.value = newPathPhaseDictValue;
          row.pathPhaseDict.valueEn = newPathPhaseDictValueEn;
        }
        setPaePositionTeam(row, newSpecialistTeamId, newSpecialistTeamName);
      }
    });
    setRefresh(!refresh);
    setPaePositions(paePositions);
  };

  const bulkUpdate = () => {
    setShowBulkEdit(true);
  };

  const bulkUpdateParams = (
    newPurchaseFormDictId?: string,
    newPurchaseFormDictValue?: string,
    newPurchaseFormDictValueEn?: string,
    newCountryDictId?: string,
    newCountryDictValue?: string,
    newCountryDictValueEn?: string,
    newActionGroup?: string,
    newBallDictId?: string,
    newBallDictValue?: string,
    newBallDictValueEn?: string,
    newPathPhaseDictId?: string,
    newPathPhaseDictValue?: string,
    newPathPhaseDictValueEn?: string,
  ) => {
    const updated = paePositions.map(row => {
      if (selectedPositions.includes(row.idx)) {
        const updatedRow = { ...row };
        if (newPurchaseFormDictId) {
          updatedRow.purchaseFormDict = {
            ...updatedRow.purchaseFormDict,
            id: newPurchaseFormDictId,
            value: newPurchaseFormDictValue!,
            valueEn: newPurchaseFormDictValueEn!,
          };
        }
        if (newCountryDictId) {
          updatedRow.countryDict = row.countryDict
            ? row.countryDict
            : GetDictionaryDtoFromJSON({});
          updatedRow.countryDict = {
            ...updatedRow.countryDict,
            id: newCountryDictId,
            value: newCountryDictValue!,
            valueEn: newCountryDictValueEn!,
          };
        }
        if (newActionGroup) {
          if (newActionGroup === t(tk.estimates.deleteActionGroup)) {
            updatedRow.actionGroup = undefined;
          } else {
            updatedRow.actionGroup = newActionGroup;
          }
        }
        if (newBallDictId) {
          updatedRow.ballDict = row.ballDict
            ? row.ballDict
            : GetDictionaryDtoFromJSON({});
          updatedRow.ballDict = {
            ...updatedRow.ballDict,
            id: newBallDictId,
            value: newBallDictValue!,
            valueEn: newBallDictValueEn!,
          };
        }
        if (newPathPhaseDictId) {
          updatedRow.pathPhaseDict = row.pathPhaseDict
            ? row.pathPhaseDict
            : GetDictionaryDtoFromJSON({});
          updatedRow.pathPhaseDict = {
            ...updatedRow.pathPhaseDict,
            id: newPathPhaseDictId,
            value: newPathPhaseDictValue!,
            valueEn: newPathPhaseDictValueEn!,
          };
        }
        return updatedRow;
      }
      return row;
    });
    setPaePositions(updated);
    setShowBulkEdit(false);
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(tk.prLang.paePositionsList, {
            projectNo: props.project?.number,
            date: momencik(props.estimateByMonth?.date, 'YYYY-MM'),
          })}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {props.purchaseOnly && (
          <Alert variant="info" className="m-0">
            {t(tk.estimates.paeImportWarning)}
          </Alert>
        )}

        {isLoading ? (
          <FormSkeleton />
        ) : (
          <>
            <div className="mb-5 mt-5">
              <div className="selected-count">
                {t(tk.component.selected)} <b>{selectedPositions.length}</b>{' '}
                {selectedPositions.length === 1
                  ? t(tk.estimates.positionAccusative1)
                  : selectedPositions.length > 1 && selectedPositions.length < 5
                    ? t(tk.estimates.positionAccusative2_4)
                    : t(tk.estimates.positionAccusative5)}
              </div>
              <PaePositionsGrid
                refresh={refresh}
                data={paePositions}
                companyId={props.project?.company?.id}
                pathPhaseEnabled={props.project?.company?.pathPhase}
                budgetId={props.project?.purchaser?.budget?.id}
                enableMultiSelect={true}
                selectedKeys={selectedPositions}
                onRowClick={handleOnRowClick}
                purchaseOnly={props.purchaseOnly}
                updateParams={updateParams}
                countriesDictionaries={props.project.projectCountries?.map(
                  o => o.countryDictionary,
                )}
                pathPhaseDictionaries={props.project.projectPathPhases?.map(
                  o => o.pathPhaseDictionary,
                )}
              />
            </div>
            <ValidationAlert
              show={showSaveErrors}
              errors={saveErrors}
              className="my-3"
            />

            <PaeBulkParameterMapperForm
              show={showBulkEdit}
              onClose={() => setShowBulkEdit(false)}
              handleUpdate={bulkUpdateParams}
              countriesDictionaries={props.project.projectCountries?.map(
                o => o.countryDictionary,
              )}
              pathPhaseDictionaries={props.project.projectPathPhases?.map(
                o => o.pathPhaseDictionary,
              )}
              pathPhaseEnabled={props.project?.company?.pathPhase}
              companyId={props.project?.company?.id}
              budgetId={props.project?.purchaser?.budget?.id}
            />
            <section className="side-by-side" style={{ marginTop: '20px' }}>
              {selectedPositions.length !==
              paePositions.filter(isPaePositionCorrect)?.length ? (
                <Button variant="primary" onClick={selectAll} className="">
                  {t(tk.component.selectAll)}
                  <Icon.CheckSquare className="icon mx-2 mb-1" />
                </Button>
              ) : (
                <Button variant="primary" onClick={deselectAll} className="">
                  {t(tk.component.deselectAll)}
                  <Icon.Square className="icon mx-2 mb-1" />
                </Button>
              )}
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.common.bulkUpdateTooltip)}</Tooltip>}
              >
                <div className="d-inline-block">
                  <Button
                    variant="primary"
                    onClick={bulkUpdate}
                    className="ms-2"
                    disabled={selectedPositions.length === 0}
                  >
                    {t(tk.common.bulkUpdate)}
                  </Button>
                </div>
              </OverlayTrigger>
              <Button
                variant="outline-success"
                className="ms-2"
                onClick={refreshPositions}
              >
                {t(tk.estimates.refreshData)}
                <Icon.ArrowClockwise className="icon mx-2 mb-1" />
              </Button>
              {paeUrl && (
                <Button
                  variant="outline-success"
                  onClick={openPae}
                  className="ms-2"
                >
                  {t(tk.estimates.openInPae)}
                  <Icon.BoxArrowUpRight className="icon mx-2 mb-1" />
                </Button>
              )}
              <div className="marginLeft">
                <Button variant="outline-secondary" onClick={props.handleClose}>
                  {t(tk.button.close)}
                </Button>
              </div>
              <div>
                {paePositions.length > 0 && (
                  <>
                    <Button
                      variant="primary"
                      onClick={importSelectedPositions}
                      className="ms-2"
                    >
                      {t(tk.estimates.importSelected)}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={importAllPositions}
                      className="ms-2"
                    >
                      {t(tk.estimates.importAll)}
                    </Button>
                  </>
                )}
              </div>
            </section>
          </>
        )}
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </Modal>
  );
};
